import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTranslation } from 'react-i18next';
import { api } from '@/services/api';
import type { LegalDocumentFull } from '@/services/api';
import { formatNumericDate } from '@/i18n/intl';
import { PendingDecision } from '@/components/editorial/PendingDecision';

interface Props { doc: string; }

/** True when the reader’s UI language is English (`en`, `en-*`). */
function isEnglish(language: string | undefined): boolean {
  return (language ?? 'en').toLowerCase().split('-')[0] === 'en';
}

// react-markdown does NOT render raw HTML unless rehype-raw is added — we
// deliberately omit it, so document bodies cannot execute embedded scripts
// (Spec 12: sanitized markdown, no HTML execution).
//
// #2109 R3-B9 (brief B13): the ONE place every legal body renders. The title
// and the body are server data and stay English; for a non-English reader one
// line in their language says so, and the English text carries `lang="en"`
// so a screen reader and the browser read it as English. The chrome around it
// (loading, version line, draft line) follows the reader’s language.
export function DocumentRenderer({ doc }: Props) {
  const { t, i18n } = useTranslation(['legal', 'common']);
  const [data, setData] = useState<LegalDocumentFull | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    api.legal.get(doc)
      .then((r) => {
        if (!active) return;
        if (r.status === 'success' && r.data) setData(r.data);
        else setFailed(true);
      })
      .catch(() => { if (active) setFailed(true); });
    return () => { active = false; };
  }, [doc]);

  if (failed) return <p className="legal-error">{t('document.unavailable')}</p>;
  if (!data) return <p className="legal-loading">{t('common:states.loading')}</p>;

  const english = isEnglish(i18n.language);

  return (
    <article className="legal-document">
      <header className="legal-document__head">
        {!english && (
          <p className="legal-document__notice" role="note" data-testid="legal-english-notice">
            {t('document.englishNotice')}{' '}
            <PendingDecision id="legal-titles-english" side="bottom" />
          </p>
        )}
        <h1 lang="en">{data.title}</h1>
        <p className="legal-document__meta">
          {t('document.meta', { version: data.version, date: formatNumericDate(data.effective_date) })}
        </p>
        <p className="legal-document__draft">
          {t('document.draft')}
        </p>
      </header>
      <div className="legal-document__body" lang="en">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{data.body_markdown}</ReactMarkdown>
      </div>
    </article>
  );
}
